import type { PageTemplate } from '@packages/recommendations/src/types';
import { logger } from '@packages/shared/src/utils/logger/logger';
import type { ParagraphLibraryType, ParagraphType } from '../../../interfaces/components';
import { ComponentTypes } from '../../../interfaces/components';
import { classes } from '../../utils/classes';
import { getComponent } from './componentRegistry';

const classesDistance = (item: ParagraphType) => {
  const distanceBehaviour =
    'behavior_paragraph_widthtype' in item && item.behavior_paragraph_widthtype;

  const distanceField = 'field_distance' in item && item.field_distance;

  const distance = distanceBehaviour || distanceField;

  switch (distance) {
    case 'fullwidth': {
      // complete browser window width
      return 'relative left-1/2 w-screen -translate-x-1/2! translate-y-0';
    }
    case 'default': {
      // yes, "default" is actually not the default. The default is an unset value (further below).
      // website width. Put it into a centered container.
      // (dont use mx-auto because if the element has a custom margin set, it won't work)
      return 'max-w-full xl:max-w-(--shop-max-width) xl:relative xl:left-1/2 xl:-translate-x-1/2';
    }
    case 'variation-1': {
      // website width with additional mobile padding
      return 'max-w-full xl:max-w-(--shop-max-width) xl:relative xl:left-1/2 xl:-translate-x-1/2 px-3 lg:px-0';
    }
    default: // no setting
      return '';
  }
};

type ComponentRecognizerProps = {
  item: ParagraphType;
  pageTemplate?: PageTemplate;
  recoIndex?: number;
  token?: string;
  className?: string;
  style?: React.CSSProperties;
  columnSizes?: string;
};

// List of components that support columnSizes prop
const supportsColumnSizes = new Set(['linkbox', 'category_teaser']);

export const ComponentRecognizer = ({
  /** The data for the single paragraph you want to render */
  item,
  /** current page you are on (needed for tracking purposes) */
  pageTemplate,
  /** For tracking: if this component is a Reco, please specify which reco it is on the page (starting with 0) */
  recoIndex = 0,
  token,
  className,
  style,
  columnSizes,
}: ComponentRecognizerProps) => {
  const Component = getComponent(item.type);

  if (Component) {
    const componentClassName = classes(classesDistance(item), className);
    const shouldPassColumnSizes = supportsColumnSizes.has(item.type);

    const componentProps = {
      data: item,
      className: componentClassName,
      pageTemplate,
      recoIndex,
      token,
      style,
      ...(shouldPassColumnSizes && columnSizes && { columnSizes }),
    };

    return <Component key={item.id} {...componentProps} />;
  }

  if ((item.type as ComponentTypes) === ComponentTypes.FromLibrary) {
    return (
      <>
        {(item as ParagraphLibraryType)?.paragraphs_library_item?.map((libItem) =>
          libItem?.paragraph?.map((libParagraphItem: ParagraphType) => (
            <ComponentRecognizer
              key={item.id}
              item={libParagraphItem}
              token={token}
              columnSizes={columnSizes}
            />
          )),
        )}
      </>
    );
  }

  if (item.type === 'content_container') {
    return (
      <>
        {item.paragraph?.map((paragraph) => (
          <ComponentRecognizer
            key={paragraph.id}
            item={paragraph}
            token={token}
            columnSizes={columnSizes}
          />
        ))}
      </>
    );
  }

  logger.warn({ type: item.type }, 'Component not found in registry');
  return null;
};
